import { Sponsorstripe } from '@sch-inventory/advertory/sites/aftenposten/index.js';
import React, { useState } from 'react';

import type { Component } from '../../../../../core/data-sources/content-components/types.js';
import type { SaveListItem } from '../../../../types/save-types.js';
import type { ArticleProps } from '../types.js';

import { Save } from '../../saving/Save.js';
import { Intro } from './Intro.js';
import Footer from '../../commonComponents/Footer.js';
import Navigation from '../../commonComponents/Navigation/Navigation.js';
import { Group } from './Group.js';
import ComponentSelector from '../articleComponents/ComponentSelector.js';
import {
  getLead,
  getLoopingVideoIntro,
  isRestaurant,
  sectionMapping,
} from '../helpers.js';
import { Recommended } from '../articleComponents/Recommended.js';
import { usePulseContext } from '../../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import Authors from '../articleComponents/Authors.js';
import SoMe from '../articleComponents/SoMe.js';
import Tags from '../articleComponents/Tags.js';
import { Newsletter } from '../../../components/newsletter/Newsletter.js';
import { LinkWithPulse } from '../../../../../core/mandatory-integrations/pulse/components/LinkWithPulse.js';
import { useListsContext } from '../../saving/ListsProvider.js';
import { useDelayedVisibility } from '../../../hooks/useDelayedVisibility.js';
import Toast from '../../commonComponents/Toast.js';

export const GuideArticle: React.FC<ArticleProps> = ({
  article,
  recommendations,
  isMobile,
  isHermesApp,
  isFeature,
  forwardedRef,
  godtTeasers,
}) => {
  const { savedItems } = useListsContext();
  const { trackClickUIElement } = usePulseContext();
  const { statusMessage, hasError } = useListsContext();
  const [showToast] = useDelayedVisibility(statusMessage, 1500, 1500);

  const title = article.title;
  const lead = getLead(article);

  const listTeaser: SaveListItem = {
    id: article.id,
    title,
    imageId: article?.mainImage?.id,
    slug: article.slug,
    kicker: article.section,
  };

  const [overlay, toggleOverlay] = useState<{
    overlayVisible: boolean;
    itemToSave?: SaveListItem;
  }>({
    overlayVisible: false,
    itemToSave: listTeaser,
  });

  const loopingVideoIntro = getLoopingVideoIntro(article);
  const articleComponents = article.components.slice(loopingVideoIntro ? 1 : 0);

  const handleOverlay = (item?: SaveListItem) => {
    trackClickUIElement({
      object: {
        id: 'article:save-button-top',
        name: 'Save button on top of guide article',
        elementType: 'Button',
      },
    }).finally(() =>
      toggleOverlay({
        overlayVisible: !overlay.overlayVisible,
        itemToSave: item,
      }),
    );
  };

  const renderItems = (components: Component[]): React.ReactNode => {
    let currentGroup: Component[] = [];
    let isGroup = false;
    let groupIndex = 0;
    const result: React.ReactNode[] = [];

    for (const [index, component] of components.entries()) {
      const intention = component.presentationProperties?.intention;

      if (intention === 'group-start') {
        currentGroup = [component];
        isGroup = true;
        groupIndex += 1;
      } else if (intention === 'group-end') {
        isGroup = false;
        result.push(
          <Group
            parentId={article.id}
            groupIndex={groupIndex}
            components={currentGroup}
            key={`group-${groupIndex}`}
            handleOverlay={handleOverlay}
            isMobile={!!isMobile}
            savedItems={savedItems}
            slug={article.slug}
            articleTitle={title}
            isGuide
            showListicleNumber={article.numbered}
          />,
        );
      } else if (isGroup) {
        currentGroup.push(component);
      } else {
        result.push(
          <ComponentSelector
            item={component}
            index={index}
            articleTitle={title}
            groupIndex={groupIndex}
            isMobile={!!isMobile}
            isGuide={true}
            key={index}
          />,
        );
      }
    }

    return result;
  };

  const classNames = [
    'guide-article',
    isFeature ? 'feature' : '',
    isMobile ? 'phone' : 'desktop',
  ].join(' ');

  const isRestaurantArticle = isRestaurant(article);

  return (
    <div className={classNames}>
      {overlay.overlayVisible && (
        <Save handleOverlay={handleOverlay} item={overlay.itemToSave} />
      )}
      {showToast && hasError && <Toast message={statusMessage} hasError />}
      <div className={isMobile ? 'mobile-article' : 'desktop'}>
        <Navigation
          fullWidth={!isMobile}
          activeSection={sectionMapping[article.section]}
          isHermesApp={isHermesApp}
          isTransparent={isFeature}
        />
        <Sponsorstripe placementId="sponsorstripe" />
        <div ref={forwardedRef}>
          <Intro
            title={title}
            lead={lead}
            handleOverlay={handleOverlay}
            item={listTeaser}
            mainImage={article?.mainImage}
            withIllustration={article.teaserWithIllustration}
            colorPalette={article.colorPalette}
            isMobile={isMobile}
            savedItems={savedItems}
            isFeature={isFeature}
            loopingVideoIntro={loopingVideoIntro}
          />
          <div className="content-container">
            <Authors
              authors={article.authors}
              published={article.published}
              updated={article.updated}
              section={sectionMapping[article.section]}
            />
            <Newsletter />
            {renderItems(articleComponents)}
            <Tags tags={article.tags} type={['byliv', 'område']} />
            {'items' in recommendations && (
              <Recommended
                recommendations={recommendations}
                isMobile={!!isMobile}
                godtTeasers={godtTeasers}
                isRestaurantSection={isRestaurantArticle}
              />
            )}
            <LinkWithPulse
              className="homescreen-button"
              href="/"
              id="redirect-to-homescreen"
              name="Redirect to homescreen"
            >
              Gå til Vink-forsiden
            </LinkWithPulse>
            {isRestaurantArticle && (
              <LinkWithPulse
                className="godt-redirect-button"
                href="https://www.godt.no/"
                id="redirect-to-godt"
                name="Redirect to Godt"
              >
                Gå til Godt.no
              </LinkWithPulse>
            )}
            <SoMe />
          </div>
        </div>
        <Footer isMobile={isMobile} />
      </div>
    </div>
  );
};
